import { cuspedArch } from "../../RoyalMahal/art/geometry";
import { between, createRng, leafPath, r2 } from "./shapes";

/*
 * The first screen's gate (scenes/GateScreen.js), kept deliberately plain: a single arch in
 * Jaipur pink under a gold finial, a doorway bordered in rani and marigold, and a genda phool
 * mala draped over it. The two peacock-teal door leaves (GateLeaf) are drawn on their own so
 * they can swing open behind the frame.
 */

const W = 600;
const TOP = 80;
const SILL = 850; // where the doors meet the step
const BOTTOM = SILL + 28; // the foot of the step, which stands on the foot of the screen

const GATE_VIEWBOX = { x: 0, y: TOP, w: W, h: BOTTOM - TOP };
const OPENING = { x: 120, y: 250, w: 360, h: SILL - 250 };
const opening = cuspedArch({ ...OPENING, spring: 172, lobes: 5 });

// The arch the doorway is set in, and the painted line just inside its edge
const OUTER = { x: 30, y: 150, w: 540, spring: 300 };
const INLINE = { x: 46, y: 172, w: 508, spring: 290 };

// The knob sits at the seam, in the middle of the doors' first square panel (GateLeaf).
const KNOB_Y = OPENING.y + 371;

const pct = (n) => `${r2(n * 100)}%`;

/** Where the door opening and the knob sit in the art, for the CSS (styles/_gate.scss). */
export const GATE_VARS = {
  "--gate-ratio": `${GATE_VIEWBOX.w} / ${GATE_VIEWBOX.h}`,
  "--op-left": pct(OPENING.x / W),
  "--op-top": pct((OPENING.y - TOP) / GATE_VIEWBOX.h),
  "--op-width": pct(OPENING.w / W),
  "--op-height": pct(OPENING.h / GATE_VIEWBOX.h),
  "--knob-top": pct((KNOB_Y - TOP) / GATE_VIEWBOX.h),
};

const LEAF_GREENS = ["#2e6b2a", "#3e7d2c", "#4f8f32", "#5fa83a"];

const cubic = (p0, p1, p2, p3, t) => {
  const u = 1 - t;
  const a = u * u * u;
  const b = 3 * u * u * t;
  const c = 3 * u * t * t;
  const d = t * t * t;
  return [a * p0[0] + b * p1[0] + c * p2[0] + d * p3[0], a * p0[1] + b * p1[1] + c * p2[1] + d * p3[1]];
};

// The left half of a smooth pointed arch, from where it springs up to its apex: the same
// curve the cusped doorway is built on, so the arch, the doorway and the mala all agree.
const archCurve = ({ x, y, w, spring }) => [
  [x, y + spring],
  [x, y + spring * 0.4],
  [x + w * 0.24, y + spring * 0.08],
  [x + w / 2, y],
];

/** A smooth pointed arch with straight legs down to `bottom`; closed along the bottom unless `open`. */
const archPath = (box, bottom, open = false) => {
  const { x, y, w, spring } = box;
  const [, a, b, apex] = archCurve(box);
  const mirror = ([px, py]) => `${r2(2 * apex[0] - px)} ${r2(py)}`;
  return (
    `M${x} ${bottom} L${x} ${y + spring} C${r2(a[0])} ${r2(a[1])} ${r2(b[0])} ${r2(b[1])} ${apex[0]} ${apex[1]}` +
    ` C${mirror(b)} ${mirror(a)} ${x + w} ${y + spring} L${x + w} ${bottom}${open ? "" : " Z"}`
  );
};

/** A marigold (or rose) from the shared symbols, centred on (x, y). */
const Flower = ({ id = "ut-marigold", x = 0, y = 0, size }) => (
  <use href={`#${id}`} x={r2(x - size / 2)} y={r2(y - size / 2)} width={r2(size)} height={r2(size)} />
);

/* ---------------------------------------------------------------- the mala ---- */

// The mala follows the arch a little outside the doorway's painted border.
const MALA = { x: 80, y: 208, w: 440, spring: 236 };
const MALA_SIZE = 28;

/** Every flower, leaf and tail of the genda mala, measured from its crown down both sides. */
const drawMala = () => {
  const rng = createRng(23);
  const curve = archCurve(MALA);

  // Evenly spaced points from the crown (t = 1) down the left side (t = 0)
  const lut = [];
  let length = 0;
  for (let i = 0; i <= 200; i += 1) {
    const p = cubic(...curve, 1 - i / 200);
    if (i) length += Math.hypot(p[0] - lut[i - 1].p[0], p[1] - lut[i - 1].p[1]);
    lut.push({ p, d: length });
  }
  const at = (d) => {
    let i = lut.findIndex((e) => e.d >= d);
    if (i < 1) i = i === 0 ? 1 : lut.length - 1;
    const a = lut[i - 1];
    const b = lut[i];
    const f = Math.min(1, Math.max(0, (d - a.d) / Math.max(1e-6, b.d - a.d)));
    const dx = b.p[0] - a.p[0];
    const dy = b.p[1] - a.p[1];
    const len = Math.hypot(dx, dy) || 1;
    // Travelling from the crown down the left side, outward (away from the doors) is to the left
    return { x: a.p[0] + dx * f, y: a.p[1] + dy * f, nx: -dy / len, ny: dx / len };
  };

  const size = MALA_SIZE;
  const step = size * 0.52;
  const count = Math.floor(length / step);
  const blooms = [];
  const leaves = [];

  for (let j = 1; j <= count; j += 1) {
    const p = at(j * step);
    const wobble = between(rng, -0.1, 0.1) * size;
    const s = size * between(rng, 0.92, 1.05);
    // Orange and yellow in runs of three, so the string reads as a classic genda mala
    const id = Math.floor(j / 3) % 2 ? "ut-marigold-yellow" : "ut-marigold";
    const d = j / count;
    const bx = p.x + p.nx * wobble;
    const by = p.y + p.ny * wobble;
    // Both sides alike, so the mala hangs evenly
    blooms.push({ x: bx, y: by, s, id, d }, { x: W - bx, y: by, s, id, d });

    if (j % 3 === 0) {
      const deg = (Math.atan2(p.ny, p.nx) * 180) / Math.PI;
      [-1, 1].forEach((side) => {
        const l = {
          x: p.x + p.nx * size * 0.2,
          y: p.y + p.ny * size * 0.2,
          rot: deg + side * between(rng, 28, 46),
          len: size * between(rng, 0.9, 1.1),
          color: LEAF_GREENS[Math.floor(rng() * LEAF_GREENS.length)],
          d,
        };
        leaves.push(l, { ...l, x: W - l.x, rot: 180 - l.rot });
      });
    }
  }

  const end = at(count * step);
  const tail = (tx) => ({
    x: tx,
    y: end.y,
    flowers: Array.from({ length: 4 }, (_, i) => ({
      y: size * 0.5 + i * size * 0.6,
      s: size * (0.86 - i * 0.04),
      id: i % 2 ? "ut-marigold-yellow" : "ut-marigold",
    })),
    tassel: size * 0.5 + 4 * size * 0.6,
  });

  return { blooms, leaves, crown: { x: W / 2, y: MALA.y }, tails: [tail(end.x), tail(W - end.x)] };
};

const MALA_ART = drawMala();

/** The tassel at the end of each of the mala's strings: a gold cap and a rani silk fringe. */
const Tassel = ({ y }) => (
  <g transform={`translate(0 ${r2(y)}) scale(1.4)`}>
    <circle r="2.6" fill="url(#ut-gold)" />
    <path d="M-5,9 C-5,3 -2.6,1.6 0,1.6 C2.6,1.6 5,3 5,9 Z" fill="#d81b60" />
    <rect x="-6" y="8.4" width="12" height="2.6" rx="1.2" fill="url(#ut-gold-h)" />
    <g stroke="#d81b60" strokeWidth="1.1" strokeLinecap="round" fill="none">
      {[-4, -2, 0, 2, 4].map((x) => (
        <path key={x} d={`M${x},11 Q${r2(x * 1.1)},16 ${r2(x * 1.35)},21`} />
      ))}
    </g>
  </g>
);

const Mala = () => (
  <g className="ut-gate__mala">
    {MALA_ART.leaves.map((l, i) => (
      <path
        key={i}
        className="ut-gate__mala-leaf"
        d={leafPath(r2(l.len), 0.36)}
        fill={l.color}
        transform={`translate(${r2(l.x)} ${r2(l.y)}) rotate(${r2(l.rot)})`}
        style={{ "--d": r2(l.d) }}
      />
    ))}

    {MALA_ART.tails.map((t, i) => (
      <g key={i} transform={`translate(${r2(t.x)} ${r2(t.y)})`}>
        <g className={`ut-gate__tail ut-gate__tail--${i ? "right" : "left"}`}>
          <line x1="0" y1="0" x2="0" y2={r2(t.tassel)} stroke="#c9962b" strokeWidth="1.2" />
          {t.flowers.map((f, k) => (
            <Flower key={k} id={f.id} y={f.y} size={f.s} />
          ))}
          <Tassel y={t.tassel} />
        </g>
      </g>
    ))}

    {MALA_ART.blooms.map((b, i) => (
      <g key={i} transform={`translate(${r2(b.x)} ${r2(b.y)})`}>
        <g className="ut-gate__bloom" style={{ "--d": r2(b.d) }}>
          <Flower id={b.id} size={b.s} />
        </g>
      </g>
    ))}

    {/* A rose at the crown, between two leaves */}
    <g transform={`translate(${MALA_ART.crown.x} ${MALA_ART.crown.y})`}>
      <g className="ut-gate__crown">
        <path d={leafPath(36, 0.38)} fill="#3e7d2c" transform="rotate(200)" />
        <path d={leafPath(36, 0.38)} fill="#4f8f32" transform="rotate(-20)" />
        <Flower id="ut-rose" size={40} />
      </g>
    </g>
  </g>
);

/* --------------------------------------------------------------- the frame ---- */

/** A small gold kalash finial, standing on the apex of the arch. */
const Finial = ({ cx, by }) => (
  <g transform={`translate(${cx} ${by})`}>
    <rect x="-14" y="-7" width="28" height="7" rx="2" fill="url(#ut-gold-h)" />
    <ellipse cy="-18" rx="12" ry="10.5" fill="url(#ut-gold)" />
    <rect x="-5" y="-32" width="10" height="5" fill="url(#ut-gold-h)" />
    <circle cy="-37" r="5" fill="url(#ut-brass)" />
    <path d="M0,-42 L2.2,-54 L0,-58 L-2.2,-54 Z" fill="url(#ut-gold)" />
  </g>
);

/**
 * The painted gateway: one arch with the doorway cut out of it, so the leaves (GateLeaf)
 * show through from behind, the mala draped over it, and a step before it.
 */
export const GateFrame = ({ className = "" }) => (
  <svg
    className={`ut-gate-art ${className}`}
    viewBox={`${GATE_VIEWBOX.x} ${GATE_VIEWBOX.y} ${GATE_VIEWBOX.w} ${GATE_VIEWBOX.h}`}
    preserveAspectRatio="xMidYMax meet"
    aria-hidden="true"
  >
    {/* The arch, with the doorway cut out of it, and a white line painted just inside its edge */}
    <path d={`${archPath(OUTER, SILL)} ${opening.d} Z`} fillRule="evenodd" fill="url(#ut-pink-stone)" />
    <path d={archPath(INLINE, SILL - 30, true)} fill="none" stroke="#fff6ee" strokeWidth="2" opacity="0.9" />
    <Finial cx={W / 2} by={OUTER.y + 2} />

    {/* The plinth it stands on, either side of the doorway */}
    {[
      [OUTER.x - 8, OPENING.x],
      [OPENING.x + OPENING.w, OUTER.x + OUTER.w + 8],
    ].map(([x1, x2]) => (
      <g key={x1}>
        <rect x={x1} y={SILL - 30} width={x2 - x1} height="30" fill="#b23d60" />
        <line x1={x1} y1={SILL - 30} x2={x2} y2={SILL - 30} stroke="url(#ut-gold-h)" strokeWidth="3" />
      </g>
    ))}

    {/* The doorway's painted border: a rani edge round a marigold band */}
    <path d={opening.d} fill="none" stroke="#c2185b" strokeWidth="30" strokeLinejoin="round" />
    <path d={opening.d} fill="none" stroke="#f5a01c" strokeWidth="19" strokeLinejoin="round" />

    {/* The step, in haldi */}
    <rect x="96" y={SILL} width={W - 192} height="22" fill="url(#ut-haldi)" />
    <rect x="96" y={SILL + 22} width={W - 192} height="6" fill="#c97f12" />
    <line x1="96" y1={SILL + 1} x2={W - 96} y2={SILL + 1} stroke="url(#ut-gold-h)" strokeWidth="2.4" />

    <Mala />
  </svg>
);

/* --------------------------------------------------------------- the doors ---- */

/** A recessed panel: a deeper teal, edged in gold. */
const Panel = ({ d }) => <path d={d} fill="url(#ut-door-panel)" stroke="#e9b949" strokeWidth="3" strokeLinejoin="round" />;

const rectPath = (x, y, w, h) => `M${x},${y} H${x + w} V${y + h} H${x} Z`;

/** One peacock-teal door leaf, viewBox 180 x 600: an arched panel under the arch and two square ones. */
export const GateLeaf = ({ side = "left", className = "" }) => (
  <svg className={`ut-gate-leaf ${className}`} viewBox="0 0 180 600" preserveAspectRatio="none" aria-hidden="true">
    <g transform={side === "right" ? "translate(180 0) scale(-1 1)" : undefined}>
      <rect width="180" height="600" fill="url(#ut-door-teal)" />
      <rect x="6" y="6" width="168" height="588" fill="none" stroke="#e9b949" strokeWidth="2" opacity="0.7" />

      <Panel d="M22,262 L22,184 C22,125 90,75 160,52 L160,262 Z" />
      <Panel d={rectPath(22, 296, 138, 150)} />
      <Panel d={rectPath(22, 480, 138, 96)} />
      <g fill="none" stroke="#f6dc8a" strokeWidth="1" opacity="0.45">
        <path d="M31,253 L31,186 C31,132 94,88 151,66 L151,253 Z" />
        <path d={rectPath(31, 305, 120, 132)} />
        <path d={rectPath(31, 489, 120, 78)} />
      </g>

      {/* The shadow down the seam where the two leaves meet */}
      <rect x="175" y="0" width="5" height="600" fill="#052c30" opacity="0.55" />
    </g>
  </svg>
);
